import type { AdminStats, UsageEvent } from "./admin-types";
import type { SimulationLimits, SimulationRecord, SimulationResult, SimulationSummary } from "./monte-carlo/types";
import type { DcmaReport, P6AnalysisRecord, P6AnalysisSummary } from "./p6/types";
import type {
  AdminSigningUser,
  CertificateAction,
  CertificateBundle,
  CertificateDocument,
  CertificatePerson,
  CertificateSummary,
  InterfaceIssueRow,
  KnownPerson,
  ParsedDocument,
  ParsedPerson,
  SigningQuota,
  SigningRequest,
  SigningSigner,
  UserProfile,
} from "./types";

/** In-memory access token (never persisted — refresh cookie is HttpOnly). */
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Called when the API rejects our token, to try a silent refresh against the
 * auth worker. Registered by AuthProvider; returns the new access token, or
 * null if the session is genuinely gone. Keeping it as a hook avoids a circular
 * import between the API client and the auth context.
 */
type RefreshHook = () => Promise<string | null>;
let refreshHook: RefreshHook | null = null;
export function setRefreshHook(hook: RefreshHook | null): void {
  refreshHook = hook;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function send(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`/api${path}`, { ...init, headers, credentials: "include" });
}

async function sendWithRefresh(path: string, init: RequestInit): Promise<Response> {
  let res = await send(path, init);

  // Access tokens live 15 minutes. On a 401 try one silent refresh and replay —
  // but only when we actually had a token, so an anonymous caller doesn't spin.
  if (res.status === 401 && accessToken && refreshHook) {
    const refreshed = await refreshHook();
    if (refreshed) res = await send(path, init);
  }
  return res;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await sendWithRefresh(path, init);

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return data as T;
}

/**
 * Like `request`, but for non-JSON responses (the HTML certificate export).
 * It must share the JSON client's auth behaviour — bearer token plus silent
 * refresh — because a bare <a href>/iframe fetch sends no Authorization header
 * and 401s in production.
 */
async function requestText(path: string, init: RequestInit = {}): Promise<string> {
  const res = await sendWithRefresh(path, init);
  const text = await res.text();
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = JSON.parse(text);
      message = data.error || data.message || message;
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new ApiError(res.status, message);
  }
  return text;
}

export interface CertificateListResponse {
  items: CertificateSummary[];
  page: number;
  perPage: number;
  total: number;
  stats: {
    active: number;
    draft: number;
    archived: number;
    total: number;
    activeLimit: number;
  };
}

export const api = {
  me: () => request<{ user: UserProfile; isAdmin: boolean; limits: Record<string, unknown> }>("/me"),

  listCertificates: (params: { q?: string; status?: string; page?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set("q", params.q);
    if (params.status) sp.set("status", params.status);
    if (params.page) sp.set("page", String(params.page));
    const qs = sp.toString();
    return request<CertificateListResponse>(`/certificates${qs ? `?${qs}` : ""}`);
  },

  createCertificate: (body: Partial<CertificateBundle["certificate"]>) =>
    request<CertificateBundle>("/certificates", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getCertificate: (id: string) => request<CertificateBundle>(`/certificates/${id}`),

  updateCertificate: (id: string, patch: Record<string, unknown>) =>
    request<CertificateBundle>(`/certificates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteCertificate: (id: string) => request<{ ok: true }>(`/certificates/${id}`, { method: "DELETE" }),

  duplicateCertificate: (id: string) => request<CertificateBundle>(`/certificates/${id}/duplicate`, { method: "POST" }),

  savePeople: (id: string, people: Partial<CertificatePerson>[]) =>
    request<{ people: CertificatePerson[] }>(`/certificates/${id}/people`, {
      method: "PUT",
      body: JSON.stringify({ people }),
    }),

  saveDocuments: (id: string, documents: Partial<CertificateDocument>[]) =>
    request<{ documents: CertificateDocument[] }>(`/certificates/${id}/documents`, {
      method: "PUT",
      body: JSON.stringify({ documents }),
    }),

  saveActions: (id: string, actions: Partial<CertificateAction>[]) =>
    request<{ actions: CertificateAction[] }>(`/certificates/${id}/actions`, {
      method: "PUT",
      body: JSON.stringify({ actions }),
    }),

  commentsRegister: () =>
    request<{
      items: InterfaceIssueRow[];
      stats: { total: number; open: number; closed: number };
    }>("/actions/register"),

  suggestPeople: (q: string) => request<{ suggestions: KnownPerson[] }>(`/people/suggest?q=${encodeURIComponent(q)}`),

  importTeams: (text: string) =>
    request<{ people: ParsedPerson[] }>("/import/teams", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  // Raw-bytes upload: Teams attendance CSVs are UTF-16, so the server does the
  // decoding — the file must not pass through client-side text conversion.
  importTeamsFile: async (file: File) =>
    request<{ people: ParsedPerson[] }>("/import/teams-file", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: await file.arrayBuffer(),
    }),

  // Remember a raw Teams name -> corrected name, so future imports auto-resolve.
  saveNameMapping: (raw_name: string, cleaned_name: string, email?: string | null) =>
    request<{ ok: true }>("/import/name-mappings", {
      method: "POST",
      body: JSON.stringify({ raw_name, cleaned_name, email: email ?? null }),
    }),

  importDocuments: (text: string) =>
    request<{ documents: ParsedDocument[] }>("/import/documents", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  // Authenticated export fetch — never link to the export URL directly; a bare
  // <a href>/iframe request carries no Authorization header.
  exportHtml: (id: string) => requestText(`/certificates/${id}/export?format=html`),

  // Word export. Binary, so it bypasses the JSON client; the filename comes
  // from the server's Content-Disposition header.
  exportDocx: async (id: string): Promise<{ blob: Blob; filename: string | null }> => {
    const res = await sendWithRefresh(`/certificates/${id}/export?format=docx`, {});
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        const data = (await res.json()) as { error?: string; message?: string };
        message = data.error || data.message || message;
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      throw new ApiError(res.status, message);
    }
    const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
    return { blob: await res.blob(), filename: match ? match[1] : null };
  },

  // --- E-signing (RailAssurance platform) ----------------------------------

  getSigningRequest: (certId: string) => request<{ request: SigningRequest | null }>(`/certificates/${certId}/signing`),

  createSigningRequest: (certId: string, signers: SigningSigner[]) =>
    request<{
      request: SigningRequest;
      method: "external" | "internal" | "magic_link";
      emailsSent?: number;
      emailsFailed?: number;
    }>(`/certificates/${certId}/signing`, {
      method: "POST",
      body: JSON.stringify({ signers }),
    }),

  getSigningQuota: () => request<SigningQuota>("/signing/quota"),

  remindSigners: (certId: string, requestId: string) =>
    request<{ request: SigningRequest; reminded: number; skipped: number; emailsSent: number }>(
      `/certificates/${certId}/signing/${requestId}/remind`,
      { method: "POST" },
    ),

  cancelSigningRequest: (certId: string, requestId: string) =>
    request<{ request: SigningRequest }>(`/certificates/${certId}/signing/${requestId}/cancel`, {
      method: "POST",
    }),

  // Authenticated binary fetch — a bare <a href> carries no Authorization header.
  downloadSignedPdf: async (certId: string, requestId: string): Promise<Blob> => {
    const res = await sendWithRefresh(`/certificates/${certId}/signing/${requestId}/pdf`, {});
    if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`);
    return res.blob();
  },

  // --- P6 Analyser ---------------------------------------------------------

  saveP6Analysis: (report: DcmaReport) =>
    request<{ analysis: P6AnalysisRecord }>("/p6/analyses", {
      method: "POST",
      body: JSON.stringify(report),
    }),

  listP6Analyses: () => request<{ items: P6AnalysisSummary[] }>("/p6/analyses"),

  getP6Analysis: (id: string) => request<{ analysis: P6AnalysisRecord }>(`/p6/analyses/${id}`),

  deleteP6Analysis: (id: string) => request<{ ok: true }>(`/p6/analyses/${id}`, { method: "DELETE" }),

  // --- Monte Carlo ---------------------------------------------------------

  getSimulationLimits: () => request<{ limits: SimulationLimits }>("/monte-carlo/limits"),

  saveSimulation: (body: {
    fileName: string | null;
    source: "csv" | "xer";
    result: SimulationResult;
  }) =>
    request<{ simulation: SimulationRecord }>("/monte-carlo/simulations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listSimulations: () => request<{ items: SimulationSummary[] }>("/monte-carlo/simulations"),

  getSimulation: (id: string) => request<{ simulation: SimulationRecord }>(`/monte-carlo/simulations/${id}`),

  deleteSimulation: (id: string) => request<{ ok: true }>(`/monte-carlo/simulations/${id}`, { method: "DELETE" }),

  // --- Admin ---------------------------------------------------------------

  adminStats: () => request<AdminStats>("/admin/stats"),

  adminConfig: () =>
    request<{
      config: Record<string, number>;
      keys: { key: string; min: number; max: number }[];
    }>("/admin/config"),

  updateAdminConfig: (patch: Record<string, number>) =>
    request<{ config: Record<string, number>; applied: Record<string, number> }>("/admin/config", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  adminSigningSettings: () =>
    request<{ configured: boolean; keyPreview: string | null; updated_at: string | null }>("/admin/signing-settings"),

  updateAdminSigningSettings: (api_key: string) =>
    request<{ configured: boolean; keyPreview: string | null; updated_at: string | null }>("/admin/signing-settings", {
      method: "PUT",
      body: JSON.stringify({ api_key }),
    }),

  adminSigningUsers: () => request<{ users: AdminSigningUser[]; defaultDailyLimit: number }>("/admin/signing-users"),

  updateAdminSigningUser: (
    userId: string,
    patch: { signing_enabled?: boolean; external_enabled?: boolean; daily_limit?: number | null },
  ) =>
    request<{
      user_id: string;
      signing_enabled: number;
      external_enabled: number;
      daily_limit: number | null;
    }>(`/admin/signing-users/${userId}`, { method: "PUT", body: JSON.stringify(patch) }),

  adminEvents: (params: { type?: string; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.type) sp.set("type", params.type);
    if (params.limit) sp.set("limit", String(params.limit));
    const qs = sp.toString();
    return request<{ items: UsageEvent[] }>(`/admin/events${qs ? `?${qs}` : ""}`);
  },
};
